import '@sass/base/_micro-animations.scss';

import { getTransitionSizes } from '@formkit/auto-animate';
import { createVAutoAnimate } from '@formkit/auto-animate/vue';

let tokens;

// the production CSS minifier rewrites time values (200ms -> .2s), so the unit must be honoured, not assumed
function parseCssTimeMs(value) {
  const trimmed = value.trim();
  const number = parseFloat(trimmed);
  if (Number.isNaN(number)) return null;
  return trimmed.endsWith('ms') ? number : number * 1000;
}

// timing values live in base/_micro-animations.scss as the single source of truth, shared with the legacy-form CSS
// animation; the fallbacks only guard against the stylesheet not having loaded yet
export function animationTokens() {
  if (!tokens) {
    const style = window.getComputedStyle(document.documentElement);
    tokens = {
      duration: parseCssTimeMs(style.getPropertyValue('--micro-animations-duration')) ?? 200,
      easing: style.getPropertyValue('--micro-animations-easing').trim() || 'ease-in-out',
      easingAdd: style.getPropertyValue('--micro-animations-easing-add').trim() || 'ease-in',
      scaleAdd: style.getPropertyValue('--micro-animations-scale-add').trim() || '0.98',
    };
  }
  return tokens;
}

// While a container is animating in (BaseModal's fade-in, Vue transitions, accordions), its contents settle
// instantly instead of micro-animating: state toggled right as a container opens (e.g. stale validation errors
// cleared or re-rendered on modal open) would otherwise replay error animations at every open. Infinite
// animations (spinners, skeletons) are ignored so they never suppress real animations. Our own micro-animations
// are ignored too: with nested v-auto-animate containers (a field inside a form group) the outer "remain" that
// makes space for a new element runs on the inner container itself, and it must not cancel the fade-in of the
// very element it is making space for.
const microAnimationEffects = new WeakSet();

function isInsideAnimatingContainer(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const animations = typeof node.getAnimations === 'function' ? node.getAnimations() : [];
    const isAnimatingIn = animations.some((animation) => {
      if (microAnimationEffects.has(animation.effect)) {
        return false;
      }
      const timing = animation.effect?.getTiming();
      return timing && timing.iterations !== Infinity;
    });
    if (isAnimatingIn) {
      return true;
    }
  }
  return false;
}

function trackedKeyframeEffect(el, keyframes, options) {
  const effect = new KeyframeEffect(el, keyframes, options);
  microAnimationEffects.add(effect);
  return effect;
}

// An element whose parent is size-animating (a field growing to fit its error) is carried by that animated layout
// already. Its own translate delta comes from cached viewport coords that go stale whenever the page shifted
// shortly before (async content, submit banners), which showed up as inputs jumping and sliding back into place.
function hasResizingParent(el) {
  const parent = el.parentElement;
  const animations = parent && typeof parent.getAnimations === 'function' ? parent.getAnimations() : [];
  return animations.some((animation) => {
    if (!microAnimationEffects.has(animation.effect)) {
      return false;
    }
    return animation.effect.getKeyframes().some((frame) => 'height' in frame || 'width' in frame);
  });
}

// Global keyframes for v-auto-animate, registered as install-time defaults (see https://auto-animate.formkit.com/#plugins).
// "add" and "remain" reproduce the library defaults. "remove" hides the leaving element instantly instead: the default
// re-inserts it with position: absolute while it fades out, which extends scroll containers (e.g. full-height modals)
// and flashes a scrollbar. Siblings still animate smoothly into the freed space via "remain".
function microAnimationKeyframes(el, action, oldCoords, newCoords) {
  if (action === 'remove') {
    return [trackedKeyframeEffect(el, [], { duration: 0 }), { styleReset: { display: 'none' } }];
  }

  if (isInsideAnimatingContainer(el)) {
    return trackedKeyframeEffect(el, [], { duration: 0 });
  }

  const { duration, easing, easingAdd, scaleAdd } = animationTokens();
  let keyframes = [];

  if (action === 'add') {
    keyframes = [
      { transform: `scale(${scaleAdd})`, opacity: 0 },
      { transform: `scale(${scaleAdd})`, opacity: 0, offset: 0.5 },
      { transform: 'scale(1)', opacity: 1 },
    ];
  }

  if (action === 'remain') {
    const [widthFrom, widthTo, heightFrom, heightTo] = getTransitionSizes(el, oldCoords, newCoords);
    const start = {};
    const end = {};
    if (widthFrom !== widthTo) {
      start.width = `${widthFrom}px`;
      end.width = `${widthTo}px`;
    }
    if (heightFrom !== heightTo) {
      start.height = `${heightFrom}px`;
      end.height = `${heightTo}px`;
    }
    // A resized element (a field growing to fit its validation error) already moves via the animated layout of
    // its own and its siblings' sizes; the stock translate on top of that double-counts the shift - and the cached
    // viewport coords behind it go stale mid-batch, making fields visibly jump and slide back when several errors
    // appear at once. Translate only elements whose size is unchanged: the genuine "slide into freed space" case.
    if (widthFrom === widthTo && heightFrom === heightTo && !hasResizingParent(el)) {
      start.transform = `translate(${oldCoords.left - newCoords.left}px, ${oldCoords.top - newCoords.top}px)`;
      end.transform = 'translate(0, 0)';
    }
    keyframes = [start, end];
  }

  // "add" runs 1.5x duration exactly like the stock library defaults: with the first half held invisible, the
  // fade-in only starts once the remain animations making space (1x duration) are nearly done
  return trackedKeyframeEffect(el, keyframes, {
    duration: action === 'add' ? duration * 1.5 : duration,
    easing: action === 'add' ? easingAdd : easing,
  });
}

const stockDirective = createVAutoAnimate(microAnimationKeyframes);

// v-auto-animate with a workaround for https://github.com/formkit/auto-animate: the library caches child positions
// synchronously on init, so mounting inside a playing Vue <transition> caches coordinates translated by the
// enter transform, and the first reflow animation replays that offset as a phantom slide. When mounted during an
// enter transition, initialisation is deferred until the transition settles; all other mounts behave stock.
export const microAnimateDirective = {
  ...stockDirective,
  mounted(el, binding) {
    const transitioningAncestor = el.closest('[class*="-enter-active"]');
    if (!transitioningAncestor) {
      stockDirective.mounted(el, binding);
      return;
    }

    let initialised = false;
    const init = () => {
      if (initialised || !el.isConnected) return;
      initialised = true;
      stockDirective.mounted(el, binding);
    };
    const { transitionDuration, transitionDelay } = window.getComputedStyle(transitioningAncestor);
    const settleMs = (parseFloat(transitionDuration) + parseFloat(transitionDelay)) * 1000;

    transitioningAncestor.addEventListener('transitionend', init, { once: true });
    setTimeout(init, settleMs + 100);
  },
};
